/**
 * Detection + reload-budget helpers for stale-deploy chunk failures.
 *
 * A Vite SPA splits each route into a content-hashed chunk. After a
 * redeploy the old hashes are gone; a tab still running the old build
 * requests a chunk the server no longer has, the SPA `index.html`
 * fallback returns it as `text/html`, and the dynamic import rejects.
 * These helpers let the router error boundary recognise that case and
 * recover by reloading once — instead of looping on a genuinely broken
 * deploy.
 */

export const CHUNK_RELOAD_KEY = "chunk-reload-attempt";
export const CHUNK_RELOAD_WINDOW_MS = 10_000;

// Covers Chrome ("Failed to fetch dynamically imported module"), Firefox
// ("error loading dynamically imported module"), Safari ("Importing a
// module script failed"), the MIME rejection ("disallowed MIME type")
// and webpack-style ChunkLoadError.
const CHUNK_ERROR_PATTERN =
  /dynamically imported module|importing a module script failed|disallowed mime type|failed to fetch dynamically imported|chunkloaderror/i;

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return CHUNK_ERROR_PATTERN.test(message);
}

/** True when we have not auto-reloaded within the dedupe window. */
export function reloadBudgetAvailable(now: number = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
    return now - last >= CHUNK_RELOAD_WINDOW_MS;
  } catch {
    // sessionStorage unavailable (private mode / SSR) — don't auto-reload.
    return false;
  }
}

/** Stamp an auto-reload so a re-failure within the window won't loop. */
export function markReloadAttempt(now: number = Date.now()): void {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
  } catch {
    /* best effort */
  }
}
